// Shared logic for "milestones awaiting company attestation".
//
// A milestone counts as pending attestation when its delivery workflow is
// ready for the seller AND the complete project has been funded.
//
// Used by "Att göra nu" on the start page and by the project list filter so the
// two always agree on what "pending attestation" means.

import type { Contract, Milestone } from '../types';
import { canAttestMilestone } from './milestoneAttestation';

export const isPayableContract = (contract: Contract) =>
  contract.draftStatus === 'signed_by_both';

const contractAmountValue = (contract: Contract) =>
  parseFloat(String(contract.contractAmount || '0').replace(/[^0-9.-]/g, '')) || 0;

export const isProjectFunded = (contracts: Contract[], projectId: string) => {
  const projectContracts = contracts.filter(
    contract => contract.projectId === projectId && isPayableContract(contract)
  );
  const totalAmount = projectContracts.reduce(
    (sum, contract) => sum + contractAmountValue(contract),
    0
  );
  const paidAmount = projectContracts.reduce(
    (sum, contract) => sum + (contract.paidAmount || 0),
    0
  );
  return totalAmount > 0 && paidAmount >= totalAmount;
};

/** Milestones that belong to a funded project and are ready for seller attestation. */
export const pendingAttestationMilestones = (
  contracts: Contract[],
  milestones: Milestone[]
): Milestone[] =>
  milestones.filter((m) => {
    if (!canAttestMilestone(m, 'company')) return false;
    const contract = contracts.find((c) => c.id === m.contractId);
    if (!contract || !isPayableContract(contract)) return false;
    return isProjectFunded(contracts, contract.projectId);
  });

/** Distinct project ids that have at least one milestone awaiting attestation. */
export const projectIdsWithPendingAttestation = (
  contracts: Contract[],
  milestones: Milestone[]
): string[] =>
  Array.from(
    new Set(
      pendingAttestationMilestones(contracts, milestones)
        .map((m) => contracts.find((c) => c.id === m.contractId)?.projectId)
        .filter((id): id is string => Boolean(id))
    )
  );
